// Performance (PRD §4.5, PP5–PP7, §3.7; rev. 1.13): planned against reported hours, and the margin
// each gives, by week, month or quarter, for a project or a person.
//
// Two charts on one shared time axis rather than one chart with two y-axes: hours (bars, plan and
// actual side by side) above margin (lines, plan dashed and actual solid). Hovering a period lights
// it in both. Colors were checked with the dataviz validator (plan #5598E7, actual #00897B: lightness,
// chroma, color-blind separation pass; the plan color is under 3:1 on white, so every value is also
// in the legend, the tooltip and the table). The plan line is dashed, so identity never rests on color.
//
// Rev. 1.16: reported hours cost two ways (§3.11). *Actual* is each person's actual hourly rate for the
// month, from their comp plan; *calculated* is the rate in effect. The view opens on actual, and the
// chart's actual line follows the choice. Rates are Base, OTE or Target (§3.12).

import { useEffect, useRef, useState } from "react";
import { Body1, Button, Caption1, Card, Input, Select, Subtitle2, Switch, Tab, TabList } from "@fluentui/react-components";
import { addDays, EXPENSE_LABEL, monthOf, splitCostDifference, today, type ExpenseCategory, type Zoom } from "../../../core";
import { useApi, type PeopleNamesResponse, type ProjectsResponse } from "../api";
import { DateInput } from "../DateInput";
import { useCodeLabels } from "../codes";
import { delta, money, pct } from "../format";
import { href } from "../router";
import { ErrorBar, Kpi, Loading, Page, useMe } from "../ui";

interface Period {
  key: string;
  label: string;
  from: string;
  to: string;
  planHours: number;
  actualHours: number;
  planMargin?: number;
  actualMargin?: number | null;
  planMarginPct?: number | null;
  actualMarginPct?: number | null;
  planCost?: number;
  actualCost?: number;
  calculatedCost?: number;
  calculatedMargin?: number | null;
  calculatedMarginPct?: number | null;
  unreportedCost?: number;
  planShared?: number;
  actualShared?: number;
  planRevenue?: number;
  actualRevenue?: number;
}
interface PersonMonth {
  personId: string;
  month: string;
  type: string | null;
  flat: boolean;
  cost: number;
  reported: number;
  expected: number;
  unpaid: number;
  rate: number;
  calculatedRate: number;
  unreported: number;
  through: string;
}
/** One person's (or placeholder's) hours and cost on one project in one period (PP11). */
interface Contributor {
  period: string;
  projectId: string;
  code: string;
  who: string;
  planHours: number;
  actualHours: number;
  planCost?: number;
  actualCost?: number;
  calculatedCost?: number;
}
interface PerfResponse {
  money: boolean;
  contributors?: Contributor[];
  /** Other costs by project and category, plan against actual (approved expense lines, ER6). */
  costs?: { period: string; projectId: string; code: string; category: string; plan: number; actual: number }[];
  roles?: { id: string; name: string }[];
  periods: Period[];
  totals: Omit<Period, "key" | "label" | "from" | "to">;
  variance?: { volume: number; rateMix: number; total: number };
  /** paid: actual cost from comp plans; calculated: no pay visibility, so actual = calculated; target. */
  actualBasis?: "paid" | "calculated" | "target";
  people?: PersonMonth[];
}

/**
 * The periods as the chart and table read them: "actual" is the chosen cost view, and fully burdened
 * takes the shared costs off both margins (rev. 1.18).
 */
function asView(p: Period, view: "actual" | "calculated", burdened = false): Period {
  const v = view === "actual" ? p : { ...p, actualCost: p.calculatedCost, actualMargin: p.calculatedMargin, actualMarginPct: p.calculatedMarginPct };
  if (!burdened) return v;
  const planMargin = (v.planMargin ?? 0) - (v.planShared ?? 0);
  const actualMargin = v.actualMargin == null ? v.actualMargin : v.actualMargin - (v.actualShared ?? 0);
  return {
    ...v,
    planMargin,
    actualMargin,
    planMarginPct: v.planRevenue ? planMargin / v.planRevenue : null,
    actualMarginPct: actualMargin != null && v.actualRevenue ? actualMargin / v.actualRevenue : null,
  };
}

const PLAN = "#5598E7";
const ACTUAL = "#00897B";
const INK = { primary: "#2B3C4D", secondary: "#46576F", muted: "#8C97A8", grid: "#E7E6E6" };
const PAD = { top: 14, right: 16, bottom: 24, left: 60 };
const h1 = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });

function useWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.floor(e.contentRect.width)));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

function ticks(lo: number, hi: number, count = 4): number[] {
  const span = hi - lo || 1;
  const raw = span / count;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= raw)!;
  const out: number[] = [];
  for (let t = Math.ceil(lo / step) * step; t <= hi + 1e-9; t += step) out.push(Math.round(t / step) * step);
  return out;
}
const compact = (n: number) => {
  const a = Math.abs(n);
  const s = a >= 1_000_000 ? `$${(a / 1_000_000).toFixed(1)}M` : a >= 1_000 ? `$${Math.round(a / 1_000)}K` : `$${Math.round(a)}`;
  return n < 0 ? `−${s}` : s;
};

export function Performance() {
  const me = useMe();
  const { label: codeLabel } = useCodeLabels();
  const [by, setBy] = useState<"project" | "person">("project");
  // Everything combined by default (rev. 1.14); pick one project or person to narrow it.
  const [projectId, setProjectId] = useState("all");
  const [personId, setPersonId] = useState("all");
  const [billable, setBillable] = useState<"all" | "billable" | "non-billable">("all");
  const [zoom, setZoom] = useState<Zoom>("month");
  const [from, setFrom] = useState(`${monthOf(addDays(today(), -180))}-01`);
  const [to, setTo] = useState(addDays(today(), 90));
  const [basis, setBasis] = useState<"base" | "ote" | "target" | "targetOte">("base");
  const atTarget = basis === "target" || basis === "targetOte";
  const [view, setView] = useState<"actual" | "calculated">("actual");
  const [burdened, setBurdened] = useState(false);
  const [chartUnit, setChartUnit] = useState<"hours" | "money">("hours");
  const [hover, setHover] = useState<number | null>(null);
  // A period picked on either chart narrows Values to it and opens who contributed (PP11).
  const [picked, setPicked] = useState<string | null>(null);
  const projects = useApi<ProjectsResponse>("/projects");
  const people = useApi<PeopleNamesResponse>("/people?view=names");
  const projectList = (projects.data?.projects ?? []).filter((p) => !p.isTemplate && ["pending", "booked", "delivered", "cancelled"].includes(p.status) && (billable === "all" || p.billable === (billable === "billable"))).sort((a, b) => a.code.localeCompare(b.code));
  const peopleList = (people.data?.people ?? []).filter((p) => p.status !== "archived");
  // A single project hidden by the billable filter falls back to all of them.
  const id = by === "project" ? (projectId === "all" || projectList.some((p) => p.id === projectId) ? projectId : "all") : personId;
  const perf = useApi<PerfResponse>(id ? `/performance?by=${by}&id=${encodeURIComponent(id)}&from=${from}&to=${to}&zoom=${zoom}&basis=${basis}&billable=${billable}` : null);
  const d = perf.data;
  const raw = d?.periods ?? [];
  const periods = raw.map((p) => asView(p, view, burdened));
  const totals = d ? asView({ ...d.totals, key: "", label: "", from: "", to: "" }, view, burdened) : null;
  const hasShared = !!d && ((d.totals.planShared ?? 0) > 0 || (d.totals.actualShared ?? 0) > 0);
  const reported = periods.filter((p) => p.actualHours > 0);
  const planToDate = reported.reduce((t, p) => t + p.planHours, 0);
  // The plan's margin over the same periods that have reported hours, so the two compare like for like.
  const planRevToDate = reported.reduce((t, p) => t + (p.planRevenue ?? 0), 0);
  const planMarginToDate = planRevToDate ? reported.reduce((t, p) => t + (p.planMargin ?? 0), 0) / planRevToDate : null;
  const pickedIndex = picked ? periods.findIndex((p) => p.key === picked) : -1;
  const pick = { selected: pickedIndex < 0 ? null : pickedIndex, onSelect: (i: number | null) => setPicked(i === null ? null : (periods[i]?.key ?? null)) };
  const allNames = new Map((people.data?.people ?? []).map((p) => [p.id, p.name]));
  const projectNames = new Map((projects.data?.projects ?? []).map((p) => [p.id, `${codeLabel(p.code)} — ${p.name}`]));

  return (
    <Page title="Performance">
      <div className="toolbar">
        <TabList size="small" selectedValue={by} onTabSelect={(_, x) => (setBy(x.value as "project" | "person"), setHover(null))} aria-label="Analyze by">
          <Tab value="project">By project</Tab>
          <Tab value="person">By person</Tab>
        </TabList>
        {by === "project" ? (
          <Select size="small" value={id} onChange={(_, x) => setProjectId(x.value)} aria-label="Project">
            <option value="all">All projects</option>
            {projectList.map((p) => (
              <option key={p.id} value={p.id}>
                {codeLabel(p.code)} — {p.name}
              </option>
            ))}
          </Select>
        ) : (
          <Select size="small" value={id} onChange={(_, x) => setPersonId(x.value)} aria-label="Person">
            <option value="all">All people</option>
            {peopleList.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        )}
        <TabList size="small" selectedValue={billable} onTabSelect={(_, x) => setBillable(x.value as "all" | "billable" | "non-billable")} aria-label="Which projects">
          <Tab value="all">All</Tab>
          <Tab value="billable">Billable</Tab>
          <Tab value="non-billable">Non-billable</Tab>
        </TabList>
        <span className="divider" />
        <TabList size="small" selectedValue={zoom} onTabSelect={(_, x) => (setZoom(x.value as Zoom), setHover(null))} aria-label="Group by">
          <Tab value="week">Weeks</Tab>
          <Tab value="month">Months</Tab>
          <Tab value="quarter">Quarters</Tab>
        </TabList>
        <label className="inline-field">
          <Caption1>From</Caption1>
          <DateInput size="small" type="date" value={from} onChange={(_, x) => x.value && setFrom(x.value)} aria-label="From" />
        </label>
        <label className="inline-field">
          <Caption1>To</Caption1>
          <DateInput size="small" type="date" value={to} onChange={(_, x) => x.value && setTo(x.value)} aria-label="To" />
        </label>
        {d?.money && (
          <>
            <span className="divider" />
            <TabList size="small" selectedValue={basis} onTabSelect={(_, x) => setBasis(x.value as "base" | "ote" | "target" | "targetOte")} aria-label="Rates">
              <Tab value="base">Base</Tab>
              <Tab value="ote">OTE</Tab>
              <Tab value="target">Target</Tab>
              <Tab value="targetOte">Target OTE</Tab>
            </TabList>
            {hasShared && (
              <TabList size="small" selectedValue={burdened ? "burdened" : "contribution"} onTabSelect={(_, x) => setBurdened(x.value === "burdened")} aria-label="Margin">
                <Tab value="contribution">Contribution</Tab>
                <Tab value="burdened">Fully burdened</Tab>
              </TabList>
            )}
            {!atTarget && (
              <TabList size="small" selectedValue={view} onTabSelect={(_, x) => setView(x.value as "actual" | "calculated")} aria-label="Cost of reported hours">
                <Tab value="actual">Actual cost</Tab>
                <Tab value="calculated">Calculated cost</Tab>
              </TabList>
            )}
          </>
        )}
      </div>
      <ErrorBar message={perf.error ?? projects.error ?? people.error} />
      {perf.loading && !d ? (
        <Loading />
      ) : !d ? null : (
        <>
          <div className="kpis" aria-label="Performance summary">
            <Kpi label="Hours to date" value={`${h1(d.totals.actualHours)} h`} sub={`against ${h1(planToDate)} h planned for the same ${zoom}s`} />
            {d.money && totals && totals.actualMargin !== undefined && (
              <Kpi
                label={view === "actual" && !atTarget ? "Margin to date, actual cost" : atTarget ? "Margin to date, target rates" : "Margin to date, calculated cost"}
                value={totals.actualMarginPct == null ? "—" : pct(totals.actualMarginPct)}
                sub={`against ${planMarginToDate == null ? "—" : pct(planMarginToDate)} planned for the same ${zoom}s${
                  !atTarget && d.totals.calculatedMarginPct != null && d.totals.actualMarginPct != null
                    ? `; ${view === "actual" ? "calculated" : "actual"} ${pct(view === "actual" ? d.totals.calculatedMarginPct : d.totals.actualMarginPct)}`
                    : ""
                }`}
              />
            )}
            {d.money && d.totals.actualHours > 0 && (d.totals.actualRevenue ?? 0) > 0 && (
              <Kpi
                label="Revenue per actual hour"
                value={money((reported.reduce((t, p) => t + (p.actualRevenue ?? 0), 0)) / d.totals.actualHours)}
                sub={(() => {
                  const planRev = reported.reduce((t, p) => t + (p.planRevenue ?? 0), 0);
                  return planToDate ? `against ${money(planRev / planToDate)} planned for the same ${zoom}s` : "";
                })()}
              />
            )}
            {d.money && burdened && hasShared && (
              <Kpi label="Shared costs carried" value={money(d.totals.actualShared ?? 0)} sub={`by reported hours; ${money(d.totals.planShared ?? 0)} by the plan`} />
            )}
            {d.money && (d.totals.unreportedCost ?? 0) > 0.5 && (
              <Kpi
                label="Unreported cost"
                value={money(d.totals.unreportedCost ?? 0)}
                tone="bad"
                sub="Paid but not carried by projects: fewer hours reported than expected"
              />
            )}
            {d.money && d.variance && (
              <>
                <Kpi label="Labor over plan: hours" value={money(d.variance.volume)} sub="(actual − planned hours) × planned rate" />
                <Kpi label="Labor over plan: rate and mix" value={money(d.variance.rateMix)} sub="(actual − planned rate) × actual hours" />
              </>
            )}
          </div>
          {!me.can("financials.read") && <Caption1 className="muted">Margin needs the financials.read capability; hours are shown.</Caption1>}

          <Card className="panel wide">
            <div className="toolbar">
              <Subtitle2>Hours{d.money ? " and margin" : ""}: plan against actual</Subtitle2>
              <span className="perf-legend" aria-label="Legend">
                <span className="perf-key">
                  <span className="perf-swatch" style={{ background: PLAN }} /> Plan
                </span>
                <span className="perf-key">
                  <span className="perf-swatch" style={{ background: ACTUAL }} /> Actual
                </span>
              </span>
            </div>
            {d.money && (
              <TabList size="small" selectedValue={chartUnit} onTabSelect={(_, x) => setChartUnit(x.value as "hours" | "money")} aria-label="Show the bars in">
                <Tab value="hours">Hours</Tab>
                <Tab value="money">$ cost</Tab>
              </TabList>
            )}
            <HoursChart periods={periods} hover={hover} onHover={setHover} unit={d.money ? chartUnit : "hours"} pick={pick} />
            {d.money && <MarginChart periods={periods} hover={hover} onHover={setHover} pick={pick} />}
            <Caption1 className="muted">
              {by === "person" && id !== "all"
                ? "By person, each project's revenue and costs are shared by hours: the plan by planned hours, the actual by reported hours."
                : "Revenue and other costs are the project's own."}{" "}
              {atTarget
                ? `Every hour, planned and reported, is costed at the target ${basis === "targetOte" ? "OTE " : ""}rate of the role it fills.`
                : view === "actual"
                  ? d.actualBasis === "paid"
                    ? "Reported hours are costed at what each person actually cost that month: a flat-paid person who works more hours costs each project less per hour. The plan is at calculated rates."
                    : "Actual cost needs pay visibility (comp.read), so reported hours here are at calculated role rates."
                  : "Reported hours are costed at each person's calculated rate on the day."}{" "}
              A period with no reported hours has no actual margin yet. Click a {zoom} to see who contributed.
            </Caption1>
          </Card>

          <Card className="panel wide">
            <div className="panel-head">
              <Subtitle2>Values{pickedIndex >= 0 ? `, ${periods[pickedIndex].label}` : ""}</Subtitle2>
              {pickedIndex >= 0 && (
                <Button size="small" appearance="subtle" onClick={() => setPicked(null)}>
                  Every {zoom}
                </Button>
              )}
            </div>
            <div className="table-scroll">
              <table className="edit-table">
                <thead>
                  <tr>
                    <th>Period</th>
                    <th className="num">Planned h</th>
                    <th className="num">Actual h</th>
                    <th className="num">Difference</th>
                    {d.money && (
                      <>
                        <th className="num">Actual cost</th>
                        <th className="num">Calculated cost</th>
                        <th className="num">Planned margin</th>
                        <th className="num">{atTarget ? "Margin at target" : view === "actual" ? "Actual margin" : "Margin, calculated"}</th>
                        <th className="num" title="Revenue recognized in the period over the hours reported">Revenue / actual h</th>
                        <th className="num">Plan %</th>
                        <th className="num">Actual %</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {periods.map((p, i) =>
                    pickedIndex >= 0 && i !== pickedIndex ? null : (
                    <tr key={p.key} className={hover === i || pickedIndex === i ? "selected" : undefined} onClick={() => pick.onSelect(pickedIndex === i ? null : i)} style={{ cursor: "pointer" }}>
                      <td>{p.label}</td>
                      <td className="num">{h1(p.planHours)}</td>
                      <td className="num">{p.actualHours ? h1(p.actualHours) : ""}</td>
                      <td className="num">{p.actualHours ? h1(p.actualHours - p.planHours) : ""}</td>
                      {d.money && (
                        <>
                          <td className="num">{p.actualHours ? money(raw[i].actualCost ?? 0) : ""}</td>
                          <td className="num">{p.actualHours ? money(raw[i].calculatedCost ?? 0) : ""}</td>
                          <td className="num">{money(p.planMargin ?? 0)}</td>
                          <td className="num">{p.actualMargin == null ? "" : money(p.actualMargin)}</td>
                          <td className="num">{p.actualHours && p.actualRevenue ? money(p.actualRevenue / p.actualHours) : ""}</td>
                          <td className="num">{p.planMarginPct == null ? "" : pct(p.planMarginPct)}</td>
                          <td className="num">{p.actualMarginPct == null ? "" : pct(p.actualMarginPct)}</td>
                        </>
                      )}
                    </tr>
                    ),
                  )}
                </tbody>
              </table>
            </div>
            {pickedIndex >= 0 && (
              <Contributors
                rows={(d.contributors ?? []).filter((c) => c.period === periods[pickedIndex].key)}
                period={periods[pickedIndex]}
                money={d.money}
                view={atTarget ? "actual" : view}
                by={id === "all" ? "all" : by}
                personName={(who) => (who.startsWith("role:") ? `Placeholder: ${d.roles?.find((r) => r.id === who.slice(5))?.name ?? who.slice(5)}` : (allNames.get(who) ?? "Someone no longer listed"))}
                projectName={(c) => projectNames.get(c.projectId) ?? codeLabel(c.code)}
              />
            )}
            {pickedIndex >= 0 && d.money && (
              <OtherCosts rows={(d.costs ?? []).filter((c) => c.period === periods[pickedIndex].key)} period={periods[pickedIndex]} projectName={(c) => projectNames.get(c.projectId) ?? codeLabel(c.code)} />
            )}
          </Card>
          {d.people && d.people.length > 0 && <PersonMonths rows={d.people} names={new Map(peopleList.map((p) => [p.id, p.name]))} />}
        </>
      )}
    </Page>
  );
}

interface Totals {
  planHours: number;
  actualHours: number;
  planCost: number;
  actualCost: number;
}
const add = (a: Totals, c: Contributor, view: "actual" | "calculated"): Totals => ({
  planHours: a.planHours + c.planHours,
  actualHours: a.actualHours + c.actualHours,
  planCost: a.planCost + (c.planCost ?? 0),
  actualCost: a.actualCost + ((view === "actual" ? c.actualCost : c.calculatedCost) ?? 0),
});
const ZERO: Totals = { planHours: 0, actualHours: 0, planCost: 0, actualCost: 0 };
const toneOf = (costOver: number) => (Math.abs(costOver) < 0.5 ? "muted" : costOver > 0 ? "delta-bad" : "delta-good");
const hoursDelta = (n: number) => delta(n, (x) => `${h1(x)} h`);

/**
 * Who contributed to a period's difference from plan (PP11, rev. 1.20): each person (or placeholder)
 * with their planned and reported hours and cost, and the cost difference split into hours and rate
 * (§3.7). Across every project, each person opens to their projects; for one person, rows are projects.
 * Biggest cost difference first, so what moved the margin is at the top.
 */
function Contributors(props: {
  rows: Contributor[];
  period: Period;
  money: boolean;
  view: "actual" | "calculated";
  by: "project" | "person" | "all";
  personName: (who: string) => string;
  projectName: (c: Contributor) => string;
}) {
  const [open, setOpen] = useState<Set<string>>(new Set());
  const groupKey = (c: Contributor) => (props.by === "person" ? c.projectId : c.who);
  const groups = new Map<string, Contributor[]>();
  for (const c of props.rows) groups.set(groupKey(c), [...(groups.get(groupKey(c)) ?? []), c]);
  const total = (cs: Contributor[]) => cs.reduce((t, c) => add(t, c, props.view), ZERO);
  const over = (t: Totals) => t.actualCost - t.planCost;
  const size = (t: Totals) => (props.money ? Math.abs(over(t)) : Math.abs(t.actualHours - t.planHours));
  const sorted = [...groups.entries()].map(([k, cs]) => ({ k, cs, t: total(cs) })).sort((a, b) => size(b.t) - size(a.t));
  const label = (k: string, cs: Contributor[]) => (props.by === "person" ? props.projectName(cs[0]) : props.personName(k));
  const link = (k: string) => (props.by === "person" ? href("projects", k) : k.startsWith("role:") ? null : href("people", k));
  const all = total(props.rows);
  const top = sorted.filter((g) => size(g.t) >= 0.5).slice(0, 3);
  const nested = props.by === "all";
  const toggle = (k: string) =>
    setOpen((o) => {
      const n = new Set(o);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  const cells = (t: Totals) => {
    const split = splitCostDifference(t.planHours, t.planCost, t.actualHours, t.actualCost);
    return (
      <>
        <td className="num">{h1(t.planHours)}</td>
        <td className="num">{h1(t.actualHours)}</td>
        <td className="num">{h1(t.actualHours - t.planHours)}</td>
        {props.money && (
          <>
            <td className="num">{money(t.planCost)}</td>
            <td className="num">{money(t.actualCost)}</td>
            <td className={`num ${toneOf(split.total)}`}>{delta(split.total, money)}</td>
            <td className="num">{delta(split.hours, money)}</td>
            <td className="num">{delta(split.rate, money)}</td>
          </>
        )}
      </>
    );
  };

  if (!props.rows.length) return <Body1 className="muted">No hours were planned or reported in {props.period.label}.</Body1>;
  return (
    <div className="contributors">
      <Subtitle2 as="h3">Who contributed in {props.period.label}</Subtitle2>
      {props.period.from <= today() && props.period.to >= today() && (
        <Caption1 className="muted">This period is still in progress: the plan covers all of it, and reported hours run to today, so hours read under plan until it ends.</Caption1>
      )}
      <Body1>
        {props.money ? (
          <>
            Labor cost <strong>{money(all.actualCost)}</strong> against <strong>{money(all.planCost)}</strong> planned: <span className={toneOf(over(all))}>{delta(over(all), money)}</span>.
          </>
        ) : (
          <>
            <strong>{h1(all.actualHours)} h</strong> reported against <strong>{h1(all.planHours)} h</strong> planned.
          </>
        )}
        {top.length > 0 && " Biggest differences: "}
        {top.map((g, n) => (
          <span key={g.k}>
            {n ? "; " : ""}
            {label(g.k, g.cs)} {props.money ? <span className={toneOf(over(g.t))}>{delta(over(g.t), money)}</span> : hoursDelta(g.t.actualHours - g.t.planHours)}
            {props.money && ` (${hoursDelta(g.t.actualHours - g.t.planHours)})`}
          </span>
        ))}
        {top.length > 0 && "."}
      </Body1>
      <div className="table-scroll">
        <table className="edit-table breakdown" aria-label={`Who contributed in ${props.period.label}`}>
          <thead>
            <tr>
              <th>{props.by === "person" ? "Project" : "Person"}</th>
              <th className="num">Planned h</th>
              <th className="num">Actual h</th>
              <th className="num">Difference</th>
              {props.money && (
                <>
                  <th className="num">Planned cost</th>
                  <th className="num">{props.view === "actual" ? "Actual cost" : "Calculated cost"}</th>
                  <th className="num">Cost over plan</th>
                  <th className="num" title="(actual − planned hours) × planned rate">
                    From hours
                  </th>
                  <th className="num" title="The rest: a different rate, or other people doing the work">
                    From rate
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.flatMap(({ k, cs, t }) => {
              const to = link(k);
              const expandable = nested && cs.length > 1;
              const isOpen = open.has(k);
              const head = (
                <tr key={k} className={expandable ? "group-row" : undefined}>
                  <th scope="row">
                    {expandable && (
                      <Button size="small" appearance="transparent" aria-expanded={isOpen} aria-label={isOpen ? "Hide projects" : "Show projects"} onClick={() => toggle(k)}>
                        {isOpen ? "▾" : "▸"}
                      </Button>
                    )}
                    {to ? <a href={to}>{label(k, cs)}</a> : label(k, cs)}
                    {nested && !expandable && <Caption1 className="muted"> · {props.projectName(cs[0])}</Caption1>}
                    {t.planHours === 0 && t.actualHours > 0 && <Caption1 className="muted"> · not on the plan</Caption1>}
                    {t.actualHours === 0 && t.planHours > 0 && <Caption1 className="muted"> · nothing reported</Caption1>}
                  </th>
                  {cells(t)}
                </tr>
              );
              if (!expandable || !isOpen) return [head];
              return [
                head,
                ...[...cs]
                  .sort((a, b) => size(add(ZERO, b, props.view)) - size(add(ZERO, a, props.view)))
                  .map((c) => (
                    <tr key={`${k}|${c.projectId}`}>
                      <th scope="row" className="indent">
                        <a href={href("projects", c.projectId)}>{props.projectName(c)}</a>
                      </th>
                      {cells(add(ZERO, c, props.view))}
                    </tr>
                  )),
              ];
            })}
            <tr className="total-row">
              <th scope="row">Total</th>
              {cells(all)}
            </tr>
          </tbody>
        </table>
      </div>
      {props.money && (
        <Caption1 className="muted">
          Cost over plan splits as the variance KPIs do: <em>from hours</em> is the extra or missing hours at the planned rate, and <em>from rate</em> is the rest, a dearer or cheaper rate or other
          people doing the work. Unplanned hours count entirely as hours.{" "}
          {props.by === "person"
            ? "For one person, each project's revenue is shared by hours, so their margin also moves with their share of the hours."
            : `Revenue is the same in plan and actual, so labor and other costs (below) make up the margin's difference${
                props.by === "all" && (props.period.unreportedCost ?? 0) > 0.5 ? `, with ${money(props.period.unreportedCost ?? 0)} of unreported cost (paid hours nobody reported)` : ""
              }.`}
        </Caption1>
      )}
    </div>
  );
}

/**
 * Other (non-labor) costs in the period, plan against actual, by project and category (PP11, ER6):
 * approved expense reports where a month has any, the plan otherwise. Billable expenses pass through
 * to the client and aren't here, since they never move margin.
 */
function OtherCosts({ rows, period, projectName }: { rows: { projectId: string; code: string; category: string; plan: number; actual: number }[]; period: Period; projectName: (c: { projectId: string; code: string }) => string }) {
  if (!rows.length) return null;
  const sorted = [...rows].sort((a, b) => Math.abs(b.actual - b.plan) - Math.abs(a.actual - a.plan));
  const plan = rows.reduce((t, r) => t + r.plan, 0);
  const actual = rows.reduce((t, r) => t + r.actual, 0);
  return (
    <div className="contributors">
      <Subtitle2 as="h3">Other costs in {period.label}</Subtitle2>
      <Caption1 className="muted">Travel, events, subcontractors and the like: from approved expense reports where the month has any, otherwise the plan. Billable expenses pass through to the client and don't appear.</Caption1>
      <div className="table-scroll">
        <table className="edit-table breakdown" aria-label={`Other costs in ${period.label}`}>
          <thead>
            <tr>
              <th>Project</th>
              <th>Category</th>
              <th className="num">Planned</th>
              <th className="num">Actual</th>
              <th className="num">Over plan</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={`${r.projectId}|${r.category}`}>
                <th scope="row">{projectName(r)}</th>
                <td>{EXPENSE_LABEL[r.category as ExpenseCategory] ?? r.category}</td>
                <td className="num">{money(r.plan)}</td>
                <td className="num">{money(r.actual)}</td>
                <td className={`num ${toneOf(r.actual - r.plan)}`}>{delta(r.actual - r.plan, money)}</td>
              </tr>
            ))}
            <tr className="total-row">
              <th scope="row">Total</th>
              <td />
              <td className="num">{money(plan)}</td>
              <td className="num">{money(actual)}</td>
              <td className={`num ${toneOf(actual - plan)}`}>{delta(actual - plan, money)}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** What each person cost by month and the actual rate it gives (EC14, §3.11). Pay data. */
function PersonMonths({ rows, names }: { rows: PersonMonth[]; names: Map<string, string> }) {
  const sorted = [...rows].sort((a, b) => (names.get(a.personId) ?? a.personId).localeCompare(names.get(b.personId) ?? b.personId) || a.month.localeCompare(b.month));
  const many = new Set(rows.map((r) => r.personId)).size > 1;
  return (
    <Card className="panel wide">
      <Subtitle2>Actual rate by month</Subtitle2>
      <Caption1 className="block">
        What each person cost the business, from their comp plan, and the rate that gives on the hours they reported (unpaid time off excluded). Exempt
        staff and monthly 1099s cost the same each month; when fewer hours are reported than expected, expected hours set the rate and the rest is
        unreported cost. The month in progress counts to today.
      </Caption1>
      <div className="table-scroll">
        <table className="edit-table compact">
          <thead>
            <tr>
              {many && <th>Person</th>}
              <th>Month</th>
              <th className="num">Cost</th>
              <th className="num">Reported h</th>
              <th className="num">Expected h</th>
              <th className="num">Actual rate</th>
              <th className="num">Calculated rate</th>
              <th className="num">Difference</th>
              <th className="num">Unreported</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((r) => (
              <tr key={`${r.personId}|${r.month}`}>
                {many && <td>{names.get(r.personId) ?? r.personId}</td>}
                <td>
                  {r.month}
                  {r.through < `${r.month}-28` && <Caption1 className="muted"> to {r.through}</Caption1>}
                </td>
                <td className="num">{money(r.cost)}</td>
                <td className="num">{h1(r.reported)}</td>
                <td className="num">{h1(r.expected)}</td>
                <td className="num">{`${r.rate.toFixed(2)}`}</td>
                <td className="num">{`${r.calculatedRate.toFixed(2)}`}</td>
                <td className="num">{r.calculatedRate ? pct(r.rate / r.calculatedRate - 1, 0) : ""}</td>
                <td className="num">{r.unreported > 0.5 ? money(r.unreported) : ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/** Shared x layout, so the two charts' periods line up exactly. */
function layout(width: number, count: number) {
  const plotW = Math.max(0, width - PAD.left - PAD.right);
  const slot = count ? plotW / count : 0;
  const every = Math.max(1, Math.ceil(count / Math.max(1, Math.floor(plotW / 56))));
  return { plotW, slot, every, cx: (i: number) => PAD.left + slot * i + slot / 2 };
}

/** Click a period to pick it (Values narrows to it and opens who contributed, PP11); click again to clear. */
interface Pick {
  selected: number | null;
  onSelect: (i: number | null) => void;
}
function pickProps(i: number, { selected, onSelect }: Pick) {
  const toggle = () => onSelect(selected === i ? null : i);
  return {
    role: "button" as const,
    "aria-pressed": selected === i,
    style: { cursor: "pointer" },
    onClick: toggle,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        toggle();
      }
    },
  };
}

/** Planned against reported, in hours or, with unit "money", in cost (CM2's $ toggle). */
function HoursChart({ periods, hover, onHover, unit = "hours", pick }: { periods: Period[]; hover: number | null; onHover: (i: number | null) => void; unit?: "hours" | "money"; pick: Pick }) {
  const planOf = (q: Period) => (unit === "money" ? (q.planCost ?? 0) : q.planHours);
  const actualOf = (q: Period) => (unit === "money" ? (q.actualHours ? (q.actualCost ?? 0) : 0) : q.actualHours);
  const fmt = (v: number) => (unit === "money" ? money(v) : `${h1(v)} h`);
  const [ref, width] = useWidth<HTMLDivElement>();
  const HEIGHT = 200;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const { plotW, slot, every, cx } = layout(width, periods.length);
  const hi = Math.max(1, ...periods.flatMap((p) => [planOf(p), actualOf(p)])) * 1.08;
  const y = (v: number) => PAD.top + (1 - v / hi) * plotH;
  const barW = Math.max(2, Math.min(18, (slot - 6) / 2 - 1));
  const bar = (x0: number, v: number) => {
    const y0 = y(0);
    const y1 = y(v);
    const h = y0 - y1;
    if (h < 0.5) return "";
    const r = Math.min(4, barW / 2, h);
    return `M${x0},${y0} V${y1 + r} Q${x0},${y1} ${x0 + r},${y1} H${x0 + barW - r} Q${x0 + barW},${y1} ${x0 + barW},${y1 + r} V${y0} Z`;
  };
  const p = hover !== null ? periods[hover] : null;
  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label={unit === "money" ? "Cost by period, planned and actual" : "Hours by period, planned and actual"}>
          {ticks(0, hi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {unit === "money" ? compact(t) : `${t} h`}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={INK.muted} strokeWidth={1} />
          {periods.map((q, i) => {
            const c = cx(i);
            const focus = hover ?? pick.selected;
            const dim = focus !== null && focus !== i ? 0.45 : 1;
            return (
              <g key={q.key}>
                {/* plan left, actual right, a 2px gap between */}
                <path d={bar(c - barW - 1, planOf(q))} fill={PLAN} opacity={dim} />
                {q.actualHours > 0 && <path d={bar(c + 1, actualOf(q))} fill={ACTUAL} opacity={dim} />}
                {i % every === 0 && (
                  <text x={c} y={HEIGHT - 6} textAnchor="middle" className="axis-text">
                    {q.label}
                  </text>
                )}
                <rect
                  x={PAD.left + slot * i}
                  y={PAD.top}
                  width={slot}
                  height={plotH}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${q.label}: ${fmt(planOf(q))} planned, ${fmt(actualOf(q))} reported. ${pick.selected === i ? "Selected; press to clear" : "Press to see who contributed"}`}
                  {...pickProps(i, pick)}
                  onPointerEnter={() => onHover(i)}
                  onPointerLeave={() => onHover(null)}
                  onFocus={() => onHover(i)}
                  onBlur={() => onHover(null)}
                />
              </g>
            );
          })}
        </svg>
      )}
      {p && hover !== null && (
        <div className="chart-tip" style={{ left: Math.min(Math.max(8, cx(hover) - 90), width - 188), top: PAD.top }} role="status">
          <strong>{p.label}</strong>
          <div className="tip-rows">
            <span>Planned {fmt(planOf(p))}</span>
            <span>Actual {p.actualHours ? `${fmt(actualOf(p))} (${actualOf(p) >= planOf(p) ? "+" : "−"}${fmt(Math.abs(actualOf(p) - planOf(p)))})` : "none yet"}</span>
            {unit === "hours" && p.actualHours > 0 && (p.actualRevenue ?? 0) > 0 && <span>Revenue per actual hour {money((p.actualRevenue ?? 0) / p.actualHours)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function MarginChart({ periods, hover, onHover, pick }: { periods: Period[]; hover: number | null; onHover: (i: number | null) => void; pick: Pick }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const HEIGHT = 180;
  const plotH = HEIGHT - PAD.top - PAD.bottom;
  const { plotW, slot, every, cx } = layout(width, periods.length);
  const values = periods.flatMap((p) => [p.planMargin ?? 0, ...(p.actualMargin == null ? [] : [p.actualMargin])]);
  const lo = Math.min(0, ...values);
  const hi = Math.max(1, ...values);
  const pad = (hi - lo) * 0.08;
  const y = (v: number) => PAD.top + ((hi + pad - v) / (hi - lo + 2 * pad)) * plotH;
  const planPath = periods.map((p, i) => `${i ? "L" : "M"}${cx(i)},${y(p.planMargin ?? 0)}`).join(" ");
  // The actual line breaks across periods with nothing reported yet.
  let actualPath = "";
  let pen = false;
  periods.forEach((p, i) => {
    if (p.actualMargin == null) {
      pen = false;
      return;
    }
    actualPath += `${pen ? "L" : "M"}${cx(i)},${y(p.actualMargin)} `;
    pen = true;
  });
  const p = hover !== null ? periods[hover] : null;
  return (
    <div className="chart" ref={ref}>
      {width > 0 && (
        <svg width={width} height={HEIGHT} role="img" aria-label="Margin by period, planned and actual">
          {ticks(lo, hi).map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} stroke={INK.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(t) + 4} textAnchor="end" className="axis-text">
                {compact(t)}
              </text>
            </g>
          ))}
          <line x1={PAD.left} x2={PAD.left + plotW} y1={y(0)} y2={y(0)} stroke={INK.muted} strokeWidth={1} />
          {(hover ?? pick.selected) !== null && <line x1={cx((hover ?? pick.selected)!)} x2={cx((hover ?? pick.selected)!)} y1={PAD.top} y2={PAD.top + plotH} stroke={INK.muted} strokeWidth={1} />}
          <path d={planPath} fill="none" stroke={PLAN} strokeWidth={2} strokeDasharray="6 4" />
          <path d={actualPath} fill="none" stroke={ACTUAL} strokeWidth={2} />
          {periods.map((q, i) =>
            q.actualMargin == null ? null : <circle key={q.key} cx={cx(i)} cy={y(q.actualMargin)} r={hover === i ? 5 : 4} fill={ACTUAL} stroke="#fff" strokeWidth={2} />,
          )}
          {periods.map((q, i) => (
            <g key={q.key}>
              {i % every === 0 && (
                <text x={cx(i)} y={HEIGHT - 6} textAnchor="middle" className="axis-text">
                  {q.label}
                </text>
              )}
              <rect
                x={PAD.left + slot * i}
                y={PAD.top}
                width={slot}
                height={plotH}
                fill="transparent"
                tabIndex={0}
                aria-label={`${q.label}: planned margin ${money(q.planMargin ?? 0)}, actual ${q.actualMargin == null ? "not yet" : money(q.actualMargin)}. ${pick.selected === i ? "Selected; press to clear" : "Press to see who contributed"}`}
                {...pickProps(i, pick)}
                onPointerEnter={() => onHover(i)}
                onPointerLeave={() => onHover(null)}
                onFocus={() => onHover(i)}
                onBlur={() => onHover(null)}
              />
            </g>
          ))}
        </svg>
      )}
      {p && hover !== null && (
        <div className="chart-tip" style={{ left: Math.min(Math.max(8, cx(hover) - 90), width - 188), top: PAD.top }} role="status">
          <strong>{p.label} margin</strong>
          <div className="tip-rows">
            <span>
              Planned {money(p.planMargin ?? 0)}
              {p.planMarginPct != null ? ` (${pct(p.planMarginPct)})` : ""}
            </span>
            <span>
              Actual {p.actualMargin == null ? "none yet" : money(p.actualMargin)}
              {p.actualMarginPct != null ? ` (${pct(p.actualMarginPct)})` : ""}
            </span>
            {p.actualCost !== undefined && p.actualHours > 0 && (
              <span>
                Labor {money(p.actualCost)} vs {money(p.planCost ?? 0)} planned
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
